import type { ModelKey, ModelLinkParams } from "./model.type";
import type { UserModelActionType } from "@/entities/action";

import { isValidId } from "@/utilities/id";
import { getIntValue } from "@/utilities/number";

import { MODEL_CONFIG_MAPPER } from "./model.constant";

export const isModelKey = (modelKey?: string): modelKey is ModelKey =>
  !!(modelKey && modelKey in MODEL_CONFIG_MAPPER);

export const extractModelFromPath = (path: string) =>
  path.split("\\")?.pop()?.toLowerCase();

export const getModelKey = (modelKey?: ModelKey | string) => {
  if (!modelKey) return undefined;

  if (isModelKey(modelKey)) return modelKey;

  // Extract modelKey from api class path
  const modelKeyExtracted = extractModelFromPath(modelKey);

  if (isModelKey(modelKeyExtracted)) return modelKeyExtracted;

  return undefined;
};

export const getModelLink = (
  modelKey: ModelKey,
  params?: ModelLinkParams
): string => {
  const model = getModelKey(modelKey);

  if (!model) return "";

  const route = MODEL_CONFIG_MAPPER[model]?.route;

  if (!route) return "";

  const id = getIntValue(params?.id ?? "");

  if (!isValidId(id)) return route;

  return `${route}/${id}`;
};

export const getModelApiKey = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.apiKey ?? model;
};

export const getModelQueryKey = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.queryKey ?? model;
};

export const getModelAuthorizationKeys = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.authorizationKeys ?? {};
};

export const getModelAuthorizationKey = (
  model: ModelKey,
  action: UserModelActionType
) => {
  return getModelAuthorizationKeys(model)?.[action];
};

export const getModelTranslationKey = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.translationKey ?? model;
};

export const isModelDeleteAsync = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.deleteAsync ?? false;
};

export const isModelRestoreSupported = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.restore ?? false;
};
